/**
 * Copies the SVG sprite symbols a recording draws into the recording itself.
 *
 * An icon rendered as `<use href="/assets/sprite.svg#id">` reaches rrweb as an absolute URL on the
 * recorded origin, and a browser refuses a cross-origin `<use>`, so played back in the workbench
 * every such icon is blank. Pointing the `use` at `#id` and appending the referenced `<symbol>`
 * elements to the recorded document makes the replay self-contained.
 */

type Attributes = Record<string, string | number | boolean | null>;

interface SerializedElement {
  type: typeof ELEMENT;
  tagName: string;
  attributes: Attributes;
  childNodes: SerializedNode[];
  isSVG?: boolean;
  id: number;
}

interface SerializedText {
  type: typeof TEXT;
  textContent: string;
  id: number;
}

interface SerializedOther {
  type: number;
  childNodes?: SerializedNode[];
  id: number;
}

type SerializedNode = SerializedElement | SerializedText | SerializedOther;

interface Addition {
  parentId: number;
  nextId: number | null;
  node: SerializedNode;
}

interface AttributeChange {
  id: number;
  attributes: Attributes;
}

interface MutationData {
  source: number;
  adds?: Addition[];
  attributes?: AttributeChange[];
}

interface RecordedEvent {
  type: number;
  data?: unknown;
}

/** A `use` reference into a sprite file, as opposed to a same-document `#id`. */
export interface SpriteReference {
  url: string;
  id: string;
}

/** Resolves a sprite URL to its markup, or null when it cannot be read. */
export type SpriteFetcher = (url: string) => Promise<string | null>;

const FULL_SNAPSHOT = 2;
const INCREMENTAL_SNAPSHOT = 3;
const MUTATION = 0;
const ELEMENT = 2;
const TEXT = 3;
const HREF_NAMES = ['href', 'xlink:href'];
// rrweb numbers a page's nodes from 1, so ids this far up cannot collide with a recorded one.
const FIRST_INJECTED_ID = 1_000_000_000;

async function fetchSprite(url: string) {
  try {
    const response = await fetch(url, { credentials: 'same-origin' });
    return response.ok ? await response.text() : null;
  } catch {
    return null;
  }
}

/**
 * Splits a `use` href into the sprite file and the symbol id.
 *
 * @param href the recorded attribute value
 * @returns the reference, or null for a same-document `#id`, an empty fragment or a non-string
 */
export function spriteReference(href: unknown): SpriteReference | null {
  if (typeof href !== 'string') return null;
  const hash = href.indexOf('#');
  if (hash <= 0 || hash === href.length - 1) return null;
  return { url: href.slice(0, hash), id: href.slice(hash + 1) };
}

function isElement(node: SerializedNode): node is SerializedElement {
  return node.type === ELEMENT;
}

function walk(node: SerializedNode, visit: (element: SerializedElement) => void) {
  if (isElement(node)) visit(node);
  const children = 'childNodes' in node ? node.childNodes : undefined;
  for (const child of children ?? []) walk(child, visit);
}

export class ReplaySpriteInliner {
  #fetchSprite: SpriteFetcher;
  #sprites = new Map<string, Promise<Map<string, Element>>>();
  #nextId = FIRST_INJECTED_ID;
  // The recorded document rrweb is currently building: reset by every full snapshot.
  #bodyId: number | null = null;
  #containerId: number | null = null;
  #inlined = new Set<string>();
  #useIds = new Set<number>();
  #queue: Promise<unknown> = Promise.resolve();

  /**
   * @param fetcher reads a sprite file; defaults to a same-origin fetch of the recorded URL
   */
  constructor(fetcher: SpriteFetcher = fetchSprite) {
    this.#fetchSprite = fetcher;
  }

  /**
   * Rewrites one chunk's events in place. Calls run one at a time in arrival order, because a
   * symbol copied into a snapshot serves the mutations of the chunks that follow it.
   *
   * @param events rrweb events, oldest first
   * @returns the same array
   */
  inline(events: unknown[]) {
    const run = this.#queue.then(() => this.#inline(events));
    this.#queue = run.catch(() => undefined);
    return run;
  }

  async #inline(events: unknown[]) {
    const recorded = events as RecordedEvent[];
    const sprites = new Map<string, Map<string, Element>>();
    for (const url of this.#spriteUrls(recorded)) sprites.set(url, await this.#symbolsOf(url));
    // Runs without a sprite reference too: the pass also follows which document is being built.
    for (const event of recorded) this.#rewrite(event, sprites);
    return events;
  }

  #spriteUrls(events: RecordedEvent[]) {
    const urls = new Set<string>();
    const useIds = new Set(this.#useIds);
    const collect = (attributes: Attributes) => {
      for (const name of HREF_NAMES) {
        const reference = spriteReference(attributes[name]);
        if (reference) urls.add(reference.url);
      }
    };
    const visit = (element: SerializedElement) => {
      if (element.tagName !== 'use') return;
      useIds.add(element.id);
      collect(element.attributes);
    };
    for (const event of events) {
      const snapshot = this.#snapshotRoot(event);
      if (snapshot) {
        useIds.clear();
        walk(snapshot, visit);
        continue;
      }
      const mutation = this.#mutation(event);
      if (!mutation) continue;
      for (const add of mutation.adds ?? []) walk(add.node, visit);
      for (const change of mutation.attributes ?? []) {
        if (useIds.has(change.id)) collect(change.attributes);
      }
    }
    return urls;
  }

  #snapshotRoot(event: RecordedEvent) {
    if (event.type !== FULL_SNAPSHOT) return null;
    return (event.data as { node?: SerializedNode } | undefined)?.node ?? null;
  }

  #mutation(event: RecordedEvent) {
    if (event.type !== INCREMENTAL_SNAPSHOT) return null;
    const data = event.data as MutationData | undefined;
    return data?.source === MUTATION ? data : null;
  }

  #symbolsOf(url: string) {
    let pending = this.#sprites.get(url);
    if (!pending) {
      pending = this.#fetchSprite(url).then((markup) => {
        const symbols = new Map<string, Element>();
        if (markup === null) {
          // Left uncached so the next chunk tries again.
          this.#sprites.delete(url);
          return symbols;
        }
        const sprite = new DOMParser().parseFromString(markup, 'image/svg+xml');
        for (const symbol of sprite.querySelectorAll('symbol[id]')) symbols.set(symbol.id, symbol);
        return symbols;
      });
      this.#sprites.set(url, pending);
    }
    return pending;
  }

  #rewrite(event: RecordedEvent, sprites: Map<string, Map<string, Element>>) {
    const snapshot = this.#snapshotRoot(event);
    if (snapshot) {
      this.#rewriteSnapshot(snapshot, sprites);
      return;
    }
    const mutation = this.#mutation(event);
    if (mutation) this.#rewriteMutation(mutation, sprites);
  }

  #rewriteSnapshot(root: SerializedNode, sprites: Map<string, Map<string, Element>>) {
    this.#bodyId = null;
    this.#containerId = null;
    this.#inlined.clear();
    this.#useIds.clear();

    let body: SerializedElement | null = null;
    let first: SerializedElement | null = null;
    const symbols: SerializedElement[] = [];
    walk(root, (element) => {
      first ??= element;
      if (element.tagName === 'body') body = element;
      this.#rewriteUse(element, sprites, symbols);
    });
    const host = body ?? first;
    if (!host) return;
    this.#bodyId = (host as SerializedElement).id;
    if (symbols.length === 0) return;

    const container = this.#container(symbols);
    (host as SerializedElement).childNodes.push(container);
    this.#containerId = container.id;
  }

  #rewriteMutation(mutation: MutationData, sprites: Map<string, Map<string, Element>>) {
    const symbols: SerializedElement[] = [];
    for (const add of mutation.adds ?? []) {
      walk(add.node, (element) => this.#rewriteUse(element, sprites, symbols));
    }
    for (const change of mutation.attributes ?? []) {
      if (this.#useIds.has(change.id)) this.#rewriteHrefs(change.attributes, sprites, symbols);
    }
    if (symbols.length === 0 || this.#bodyId === null) return;

    mutation.adds ??= [];
    if (this.#containerId === null) {
      const container = this.#container([]);
      mutation.adds.push({ parentId: this.#bodyId, nextId: null, node: container });
      this.#containerId = container.id;
    }
    for (const symbol of symbols) this.#appendFlattened(mutation.adds, this.#containerId, symbol);
  }

  // The player builds each added node without its children (the recorder emits one add per
  // node), so a copied subtree goes in one node at a time, parents first.
  #appendFlattened(adds: Addition[], parentId: number, node: SerializedNode) {
    if (!isElement(node)) {
      adds.push({ parentId, nextId: null, node });
      return;
    }
    adds.push({ parentId, nextId: null, node: { ...node, childNodes: [] } });
    for (const child of node.childNodes) this.#appendFlattened(adds, node.id, child);
  }

  #rewriteUse(
    element: SerializedElement,
    sprites: Map<string, Map<string, Element>>,
    symbols: SerializedElement[]
  ) {
    if (element.tagName !== 'use') return;
    this.#useIds.add(element.id);
    this.#rewriteHrefs(element.attributes, sprites, symbols);
  }

  #rewriteHrefs(
    attributes: Attributes,
    sprites: Map<string, Map<string, Element>>,
    symbols: SerializedElement[]
  ) {
    for (const name of HREF_NAMES) {
      const reference = spriteReference(attributes[name]);
      const symbol = reference && sprites.get(reference.url)?.get(reference.id);
      if (!reference || !symbol) continue;
      attributes[name] = `#${reference.id}`;
      if (this.#inlined.has(reference.id)) continue;
      this.#inlined.add(reference.id);
      symbols.push(this.#serialize(symbol));
    }
  }

  #container(childNodes: SerializedElement[]): SerializedElement {
    return {
      type: ELEMENT,
      tagName: 'svg',
      attributes: {
        xmlns: 'http://www.w3.org/2000/svg',
        'aria-hidden': 'true',
        style: 'position:absolute;width:0;height:0;overflow:hidden',
        'data-replay-sprite': '',
      },
      childNodes,
      isSVG: true,
      id: this.#nextId++,
    };
  }

  // The shape rrweb records: lower-cased tag names (its player maps them back to the SVG
  // casing), attributes by name, `isSVG` so the player creates the nodes in the SVG namespace.
  #serialize(element: Element): SerializedElement {
    const childNodes: SerializedNode[] = [];
    for (const child of element.childNodes) {
      if (child instanceof Element) {
        childNodes.push(this.#serialize(child));
      } else if (child.nodeType === Node.TEXT_NODE && child.textContent) {
        childNodes.push({ type: TEXT, textContent: child.textContent, id: this.#nextId++ });
      }
    }
    return {
      type: ELEMENT,
      tagName: element.tagName.toLowerCase(),
      attributes: Object.fromEntries(
        Array.from(element.attributes, (attribute) => [attribute.name, attribute.value])
      ),
      childNodes,
      isSVG: true,
      id: this.#nextId++,
    };
  }
}
